/**
 * Web tab of an event: what the online events page publishes — the cover image (VenueEvent.image,
 * uploaded like a company or category image), the public title (the event name), the public
 * description (registrationInfo, localizable) — and a preview of the resulting event page.
 */
class EventDetailWebViewController extends MUIViewController
{
    private coverImageView:MUIImageView = null;
    private titleLabel:MUILabel = null;
    private dateLabel:MUILabel = null;
    private descriptionTextArea:MUITextArea = null;
    private descriptionLanguageButton:MUIButton = null;
    private priceLabel:MUILabel = null;
    private placesLabel:MUILabel = null;

    private previewCover:MUIView = null;
    private previewKickerLabel:MUILabel = null;
    private previewTitleLabel:MUILabel = null;
    private previewMetaLabel:MUILabel = null;
    private previewDescriptionLabel:MUILabel = null;
    private previewCTALabel:MUILabel = null;

    private df:MIODateFormatter = MUIWebApplication.sharedInstance().delegate.dateFormatter;
    private tf:MIODateFormatter = MUIWebApplication.sharedInstance().delegate.timeFormatter;
    private ltf:MIODateFormatter = MUIWebApplication.sharedInstance().delegate.longDateFormatter;
    private cf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.currencyFormatter;
    private nf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.integerFormatter;

    viewDidLoad(){
        super.viewDidLoad();

        this.coverImageView = MUIOutlet(this, 'cover-img', 'MUIImageView');
        let tapGesture = new MUITapGestureRecognizer();
        tapGesture.initWithTarget(this, this.imageTapGesture);
        this.coverImageView.addGestureRecognizer(tapGesture);

        this.titleLabel = MUIOutlet(this, 'title-lbl', 'MUILabel');
        this.dateLabel = MUIOutlet(this, 'date-lbl', 'MUILabel');
        this.priceLabel = MUIOutlet(this, 'price-lbl', 'MUILabel');
        this.placesLabel = MUIOutlet(this, 'places-lbl', 'MUILabel');

        this.descriptionTextArea = MUIOutlet(this, 'description-ta', 'MUITextArea');
        this.descriptionTextArea.setOnChangeText(this, function(this:EventDetailWebViewController, control:MUITextArea, value:string){
            this._event.registrationInfo = value.length > 0 ? value.trim() : null;
            this.updatePreview();
        });

        this.descriptionLanguageButton = MUIOutlet(this, 'description-language-btn', 'MUIButton');
        this.descriptionLanguageButton.setAction(this, function(this:EventDetailWebViewController){
            let vc = ProductLanguageViewController.newInstance();
            vc.setItem(this._event, "registrationInfo", true);
            vc.modalSize = new MIOSize(800, 400);
            this.presentViewController(vc, true);
        });

        this.previewCover = MUIOutlet(this, 'preview-cover-img', 'MUIView');
        this.previewKickerLabel = MUIOutlet(this, 'preview-kicker-lbl', 'MUILabel');
        this.previewTitleLabel = MUIOutlet(this, 'preview-title-lbl', 'MUILabel');
        this.previewMetaLabel = MUIOutlet(this, 'preview-meta-lbl', 'MUILabel');
        this.previewDescriptionLabel = MUIOutlet(this, 'preview-description-lbl', 'MUILabel');
        this.previewCTALabel = MUIOutlet(this, 'preview-cta-lbl', 'MUILabel');
    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private _event:VenueEvent = null;
    set event(value:VenueEvent){
        this._event = value;
        this.updateUI();
    }

    private updateUI(){
        if (this.viewIsLoaded == false || this._event == null) return;

        this.coverImageView.setImage(this._event.image?.url);
        this.titleLabel.text = this._event.name;
        this.dateLabel.text = this.dateTimeText();
        this.descriptionTextArea.text = this._event.registrationInfo;
        this.priceLabel.text = this.priceText();
        this.placesLabel.text = this.placesText();
        this.updatePreview();
    }

    // What the events page shows for this event, with the information it has today
    private updatePreview(){
        let url = this._event.image?.url;
        this.previewCover.layer.style.backgroundImage = url != null ? "url('" + url + "')" : "";
        this.previewKickerLabel.text = this._event.beginDateTime != null ? this.ltf.stringFromDate(this._event.beginDateTime) : "";
        this.previewTitleLabel.text = this._event.name;
        this.previewMetaLabel.text = [this.timeRangeText(), this.priceText(), this.placesText()].filter(x => x != null && x.length > 0).join(" · ");
        this.previewDescriptionLabel.text = this._event.registrationInfo ?? "";
        this.previewCTALabel.text = (this._event.price || 0) > 0 ? MIOLocalizeString("BUY_TICKETS", "Buy tickets") : MIOLocalizeString("REGISTER", "Register");
    }

    private dateTimeText():string {
        let b = this._event.beginDateTime;
        if (b == null) return "";
        return this.df.stringFromDate(b) + " · " + this.timeRangeText();
    }

    private timeRangeText():string {
        let b = this._event.beginDateTime, e = this._event.endDateTime;
        if (b == null) return "";
        return this.tf.stringFromDate(b) + (e != null ? " - " + this.tf.stringFromDate(e) : "");
    }

    private priceText():string {
        let price = this._event.price || 0;
        return price > 0 ? this.cf.stringFromNumber(price) + " / " + MIOLocalizeString("PERSON", "person") : MIOLocalizeString("FREE", "Free");
    }

    private placesText():string {
        let max = this._event.maxPax || 0;
        return max > 0 ? this.nf.stringFromNumber(max) + " " + MIOLocalizeString("PLACES", "places") : MIOLocalizeString("UNLIMITED_PLACES", "Unlimited places");
    }

    private imageTapGesture(gesture:MUIGestureRecognizer){
        if (gesture.state != MUIGestureRecognizerState.Began) return;

        AppHelper.sharedInstance().showImageSelectorPanel(null, (image:DBImage, context:any) => {
            this._event.image = image;
            this.coverImageView.setImage(image?.url);
            this.updatePreview();
        });
    }
}
